import { debounce } from "lodash";
import { useState, RefObject, useCallback, useEffect } from "react";

interface Config<T> {
  cache?: { width?: number; height?: number };
  externalRefObject?: RefObject<T>;
  externalRef?: T;
}

const useComponentDimensionsGen = <T extends HTMLDivElement | HTMLImageElement>(
  config?: Config<T>
): {
  width: number;
  height: number;
  measure(el: T): void;
} => {
  const { cache } = config || {};

  const [node, setNode] = useState<T | null>(null);
  const [mounted, setMounted] = useState(false);

  const [dimensions, setDimensions] = useState({
    width: cache?.width || 0,
    height: cache?.height || 0
  });

  const measure = useCallback(
    debounce((el: T) => {
      if (el) {
        const { width, height } = el.getBoundingClientRect();

        if (width && height) setDimensions({ width, height });

        setNode(el);
      }
    }, 0),
    []
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const measureCurrentNode = () => {
      if (node) measure(node);
    };

    measureCurrentNode();

    if (mounted) node?.addEventListener("resize", measureCurrentNode);

    return () => {
      if (mounted) node?.removeEventListener("resize", measureCurrentNode);
    };
  }, [node, mounted]);

  return { ...dimensions, measure };
};

export default useComponentDimensionsGen;
